// "View calls" (2026-10-27; docs/contact-view.md): one small link per step
// in place of a row of "View call: <label>" links. It opens one modal that
// lists each call collapsed, by its label, with an "Expand all" toggle.
// Admin only: the caller renders it only for an admin. Pure helpers here so
// node --test can cover them (ui/test/viewCalls.test.js).
import { foundChipLabel } from './pendingLabels.js'

// The link's text: "View call" for one call, "View calls (N)" for several.
export function viewCallsLinkText(count) {
  return count === 1 ? 'View call' : `View calls (${count})`
}

// The calls to list: entries with a call, each with a label. An entry is
// { label, call }; a missing label falls back to the step's title (one
// call), then to the call's own service and method.
export function viewCallEntries(entries = [], { title = null } = {}) {
  const list = (entries ?? []).filter((e) => e?.call)
  return list.map((e, i) => ({
    call: e.call,
    label: e.label || e.call.label || (list.length === 1 && title) || fallbackLabel(e.call, i),
    // A call the Activity Log recorded (its response and error detail show).
    logged: Boolean(e.logged),
    // Its outcome, for the row's status chip (callStatus, 2026-10-29). Only
    // a recorded call carries one; a planned call has none.
    ...(e.logged ? { found: e.call.found, error: e.call.error, errorMessage: e.call.errorMessage } : {}),
  }))
}

// A call's status chip (2026-10-29), the same in the Activity Log row and
// the View calls modal row (CallStatusChip.jsx): 'error', 'found',
// 'not_found', 'not_ready', or null for no chip. Only a recorded call (logged) gets one;
// a planned call on a step that has not run yet never reads "not found".
export function callStatus(call, { logged = true } = {}) {
  if (!logged || !call) return null
  if (call.error) return 'error'
  if (call.found === undefined) return null
  // A read that came back empty while the OMS API is still working says
  // "not ready yet", never "not found" (foundChipLabel, 2026-10-29).
  if (foundChipLabel(call) === 'not ready yet') return 'not_ready'
  return call.found ? 'found' : 'not_found'
}

function fallbackLabel(call, i) {
  const parts = [call.service, call.method].filter(Boolean)
  return parts.length ? parts.join(' ') : `Call ${i + 1}`
}

// Expanded state: a Set of indexes. When the modal opens, a lone call
// starts expanded and several start collapsed (2026-10-27).
export function initialExpanded(count) {
  return count === 1 ? new Set([0]) : new Set()
}

export function toggleExpanded(expanded, index) {
  const next = new Set(expanded)
  if (next.has(index)) next.delete(index)
  else next.add(index)
  return next
}

export function allExpanded(expanded, count) {
  return count > 0 && expanded.size >= count
}

// The "Expand all" toggle: expands every call, or collapses every call when
// all are already open.
export function toggleExpandAll(expanded, count) {
  return allExpanded(expanded, count) ? new Set() : new Set(Array.from({ length: count }, (_, i) => i))
}

export function expandAllText(expanded, count) {
  return allExpanded(expanded, count) ? 'Collapse all' : 'Expand all'
}

// Activity Log button text (2026-10-29): "View API call" for one call,
// "View N API calls" for several. Steps keep viewCallsLinkText above.
export function activityViewCallsText(count) {
  return count === 1 ? 'View API call' : `View ${count} API calls`
}

// Expanded state when a call is opened from its own row (the Activity Log,
// 2026-10-29): only that call is expanded.
export function expandedForIndex(count, index) {
  return Number.isInteger(index) && index >= 0 && index < count ? new Set([index]) : initialExpanded(count)
}

// The modal's size (2026-10-29): it opens wide, the person can resize it,
// and their last size (not position) is kept in localStorage under one key.
export const VIEW_CALLS_SIZE_KEY = 'cxhub.viewCalls.size'
export const VIEW_CALLS_MIN = { width: 480, height: 300 }

// Default: 80% of the viewport wide (at least 640px, at most 1400px, never
// wider than the viewport) and 80% of its height.
export function defaultModalSize(vw, vh) {
  const width = Math.min(Math.max(Math.round(vw * 0.8), 640), 1400, Math.max(vw, VIEW_CALLS_MIN.width))
  const height = Math.round(vh * 0.8)
  return { width, height: Math.max(height, Math.min(VIEW_CALLS_MIN.height, vh)) }
}

// A saved size clamped to the current viewport (and the minimum). Anything
// that is not two finite numbers is null.
export function clampModalSize(size, vw, vh) {
  const w = Number(size?.width)
  const h = Number(size?.height)
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null
  return {
    width: Math.round(Math.max(Math.min(w, vw), Math.min(VIEW_CALLS_MIN.width, vw))),
    height: Math.round(Math.max(Math.min(h, vh), Math.min(VIEW_CALLS_MIN.height, vh))),
  }
}

// The size to open at: the saved one, clamped, or the default. Storage may
// be missing or throw (a private window, blocked site data).
export function loadModalSize(storage, vw, vh) {
  try {
    const raw = storage?.getItem(VIEW_CALLS_SIZE_KEY)
    const saved = raw ? clampModalSize(JSON.parse(raw), vw, vh) : null
    if (saved) return saved
  } catch { /* fall through to the default */ }
  return defaultModalSize(vw, vh)
}

export function saveModalSize(storage, size) {
  try {
    const w = Math.round(Number(size?.width))
    const h = Math.round(Number(size?.height))
    if (!Number.isFinite(w) || !Number.isFinite(h)) return
    storage?.setItem(VIEW_CALLS_SIZE_KEY, JSON.stringify({ width: w, height: h }))
  } catch { /* not kept; the next open uses the default */ }
}

export function clearModalSize(storage) {
  try { storage?.removeItem(VIEW_CALLS_SIZE_KEY) } catch { /* nothing kept */ }
}

// A recorded block's text for the pre and its copy button.
export function blockText(value) {
  if (value === undefined) return ''
  if (typeof value === 'string') return value
  return JSON.stringify(value, null, 2)
}
